import liff from "@line/liff";
import { backupLocationForLoginRedirect } from "./line/liffStateDeepLink";

/**
 * LINE access token 失效的偵測與復原。
 *
 * 背景：lineAccessToken 只在 LIFF 初始化時取一次就存進 sessionStorage 用到底，沒有刷新機制。
 * token 失效後每一支 API 都 401，使用者只看到「登入逾時，請重新開啟頁面後再試」而無法自救。
 *
 * 實測（2026-07-21，dev）失效原因是 **revoked** 而不是 expired：
 *   {error: 'invalid_request', error_description: 'The access token revoked'}
 * 同一個 LINE Login channel 在別處重新登入會撤銷前一個 access token——本專案兩個 LIFF app
 * （…-8GTOgCk9 / …-0QY3e0VD）共用 channel 2004410266，手機與桌機同時開就會互相撤銷。
 *
 * 兩個關鍵設計：
 *
 * 1. 不靠後端訊息判斷，直接問 LINE。
 *    後端 LineUserGuard 的 'Failed to verify access token' 是兜底 catch，token 壞掉、
 *    Lambda 連不到 LINE、DB 查詢出錯全都掉進同一句，分不出來。誤判的代價是把
 *    「後端出事」當成「token 壞掉」而登出使用者。改為呼叫 LINE 的 verify 端點取權威答案，
 *    順帶自動排除「未註冊」那種 token 有效的 401（不必再維護訊息白名單）。
 *
 * 2. 必須先 logout 再 login。
 *    LIFF 的 liff.login() 在已登入狀態下是空操作（官方文件：若使用者已登入則不做任何事）。
 *    而 token 被撤銷時 liff.isLoggedIn() 仍為 true，只呼叫 login() 會靜默地什麼都沒發生。
 */

/** 同一個 session 內兩次自動復原的最短間隔（毫秒）——防止跳轉迴圈。 */
export const RECOVER_MIN_INTERVAL_MS = 60_000;
const RECOVER_AT_KEY = "lineSessionRecoverAt";

export const isUnauthorized = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | undefined)?.response?.status === 401;

/**
 * 問 LINE 這個 token 還能不能用。
 *
 * @returns true＝仍有效（401 是別的原因，例如使用者未註冊，不可登出）；
 *          false＝已失效（過期或被撤銷）；
 *          null＝問不到（網路/CORS 失敗）→ 一律當作有效，寧可少做也不要誤登出。
 */
export const verifyLineAccessToken = async (token: string): Promise<boolean | null> => {
  if (!token) return false;
  try {
    const res = await fetch(`https://api.line.me/oauth2/v2.1/verify?access_token=${encodeURIComponent(token)}`);
    if (res.status === 400) return false; // invalid_request：token 無效或已被撤銷
    if (!res.ok) return null;
    const data = (await res.json()) as { expires_in?: number };
    return typeof data.expires_in === "number" ? data.expires_in > 0 : null;
  } catch {
    return null;
  }
};

/**
 * 判斷現在能不能自動復原。剛跳轉回來又立刻失敗就不再跳，讓呼叫端顯示錯誤，
 * 總比把使用者關進無限跳轉好。
 */
export const canRecover = (now: number, lastAt: number | null): boolean =>
  lastAt === null || now - lastAt >= RECOVER_MIN_INTERVAL_MS;

const readLastRecoverAt = (): number | null => {
  const raw = sessionStorage.getItem(RECOVER_AT_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * token 確認失效 → 清掉 LIFF session 後重新登入，並帶回目前這一頁的完整網址
 * （不帶 redirectUri 的話 LINE 會把人丟回 LIFF 端點根路徑，query 參數全掉）。
 *
 * 注意 redirectUri 必須在該 LIFF app 註冊的端點網域底下，localhost 開發時會被 LINE 拒絕。
 *
 * @returns true＝已觸發復原；false＝token 仍有效、問不到，或剛跳過（節流中）。
 */
export const recoverLineSession = async (token: string): Promise<boolean> => {
  if (!canRecover(Date.now(), readLastRecoverAt())) return false;
  const valid = await verifyLineAccessToken(token);
  if (valid !== false) return false; // 有效或問不到 → 不動作
  sessionStorage.setItem(RECOVER_AT_KEY, String(Date.now()));
  // 出發前把當下位置寫成備援並蓋旅程章（2026-09-09 15:22 實錘：這趟重登的回跳可能
  // 落在端點根路徑而不是 redirectUri，目的地全靠備援救回；章讓回跳開機認得這張紙條）。
  try {
    backupLocationForLoginRedirect(window.location.pathname, window.location.search);
  } catch {
    // 備援寫入失敗不阻擋重登
  }
  // 先登出：token 被撤銷時 liff.isLoggedIn() 仍為 true，直接 login() 會是空操作
  try {
    liff.logout();
  } catch {
    // logout 失敗不阻擋後續登入
  }
  liff.login({ redirectUri: window.location.href });
  return true;
};
